/**
 * @atlas
 * @kind client
 * @partOf primitive:ui, primitive:websockets
 * @uses primitive:shared
 */
import type { QueryClient } from '@tanstack/react-query';
import { captureError } from '@zealot/monitoring/browser';
import { createWebSocketClient, type WSEvent } from '@zealot/shared/ws';
import { dispatchMessage } from '@zealot/ui/lib/ws/dispatch';

export type ApiWebsocketTiming = {
  heartbeatMs: number;
  pongTimeoutMs: number;
  reconnectAckTimeoutMs: number;
  retryBaseMs: number;
  retryMaxMs: number;
};

const DEFAULT_TIMING: ApiWebsocketTiming = {
  heartbeatMs: 30_000,
  pongTimeoutMs: 5_000,
  reconnectAckTimeoutMs: 5_000,
  retryBaseMs: 1_000,
  retryMaxMs: 30_000,
};

export type ApiWebsocket = {
  connect: () => void;
  close: () => void;
  syncIdentity: () => Promise<void>;
  subscribe: (channel: string) => void;
  unsubscribe: (channel: string) => void;
};

type InboundFrame = {
  type?: string;
  action?: string;
  channel?: string;
  category?: string;
};

const toIdentityFrame = (token: string | null): Record<string, unknown> =>
  token ? { action: 'authenticate', headers: { authorization: `Bearer ${token}` } } : { action: 'logout' };

// gloss
export const createApiWebsocket = (
  url: string,
  queryClient: QueryClient,
  getToken: () => Promise<string | null | undefined>,
  onChannelsLive?: (channels: string[]) => void,
  timingOverrides: Partial<ApiWebsocketTiming> = {},
): ApiWebsocket => {
  const timing = { ...DEFAULT_TIMING, ...timingOverrides };
  const channels = new Map<string, number>();
  const sentSubscribes = new Set<string>();
  let identityAttempts = 0;
  let sentToken: string | null | undefined;
  let identityChain: Promise<void> = Promise.resolve();
  let everOpened = false;
  let heartbeat: ReturnType<typeof setInterval> | undefined;
  let pongTimer: ReturnType<typeof setTimeout> | undefined;
  let pendingAcks: Set<string> | null = null;
  let reconnectAckTimer: ReturnType<typeof setTimeout> | undefined;
  let syncRetryTimer: ReturnType<typeof setTimeout> | undefined;
  let resendRetryTimer: ReturnType<typeof setTimeout> | undefined;

  const backoffDelay = (attempt: number): number => {
    const ceiling = Math.min(timing.retryMaxMs, timing.retryBaseMs * 2 ** (attempt - 1));
    return ceiling / 2 + Math.random() * (ceiling / 2);
  };

  const readToken = async (): Promise<{ token: string | null } | null> => {
    try {
      return { token: (await getToken()) ?? null };
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'apiWebsocket.getToken' });
      return null;
    }
  };

  const retryLater = (pending: ReturnType<typeof setTimeout> | undefined, retry: () => Promise<void>) => {
    identityAttempts++;
    clearTimeout(pending);
    return setTimeout(() => void retry(), backoffDelay(identityAttempts));
  };

  const retrySyncLater = (): void => {
    syncRetryTimer = retryLater(syncRetryTimer, syncIdentity);
  };

  const retryResendLater = (): void => {
    resendRetryTimer = retryLater(resendRetryTimer, resendIdentity);
  };

  const isReady = (): boolean => socket.status() === 'open' && sentToken !== undefined;

  const sendSubscribe = (channel: string): void => {
    socket.send({ action: 'subscribe', channel });
    sentSubscribes.add(channel);
  };

  const sendRelease = (frame: Record<string, unknown>): void => {
    if (socket.status() === 'open') socket.send(frame);
  };

  const replaySubscriptions = (): void => {
    if (!isReady()) return;
    for (const channel of channels.keys()) sendSubscribe(channel);
  };

  const sendIdentity = (token: string | null): void => {
    sentToken = token;
    socket.send(toIdentityFrame(token));
    replaySubscriptions();
  };

  const serializeIdentity = (task: () => Promise<void>): Promise<void> => {
    identityChain = identityChain.then(task);
    return identityChain;
  };

  const syncIdentity = (): Promise<void> =>
    serializeIdentity(async () => {
      const read = await readToken();
      if (!read) return retrySyncLater();
      identityAttempts = 0;
      if (socket.status() !== 'open' || read.token === sentToken) return;
      sendIdentity(read.token);
    });

  const resendIdentity = (): Promise<void> =>
    serializeIdentity(async () => {
      const read = await readToken();
      if (!read) return retryResendLater();
      if (socket.status() === 'open') sendIdentity(read.token);
    });

  const finishReconnect = (): void => {
    if (!pendingAcks) return;
    pendingAcks = null;
    clearTimeout(reconnectAckTimer);
    onChannelsLive?.([...channels.keys()]);
  };

  const settleReconnectAck = (channel: string): boolean => {
    if (!pendingAcks?.delete(channel)) return false;
    if (pendingAcks.size === 0) finishReconnect();
    return true;
  };

  const onSubscribed = (channel: string): void => {
    if (settleReconnectAck(channel) || !channels.has(channel)) return;
    onChannelsLive?.([channel]);
  };

  const onFrameError = (frame: InboundFrame): void => {
    if (frame.action === 'authenticate' || frame.action === 'logout') return void retryResendLater();
    const { channel } = frame;
    if (frame.action === 'subscribe' && channel) {
      setTimeout(() => {
        if (channels.has(channel) && isReady()) sendSubscribe(channel);
      }, timing.retryBaseMs);
      return;
    }
    if (frame.action === 'unsubscribe' && channel) {
      setTimeout(() => {
        if (!channels.has(channel)) sendRelease({ action: 'unsubscribe', channel });
      }, timing.retryBaseMs);
    }
  };

  const onControlFrame = (frame: InboundFrame): void => {
    switch (frame.type) {
      case 'pong':
        return void clearTimeout(pongTimer);
      case 'subscribeRejected':
        sentSubscribes.delete(frame.channel as string);
        return void settleReconnectAck(frame.channel as string);
      case 'subscribed':
        return void onSubscribed(frame.channel as string);
      case 'identity':
        identityAttempts = 0;
        return;
      case 'error':
        return void onFrameError(frame);
    }
  };

  const socket = createWebSocketClient({
    url,
    onMessage: (data) => {
      const frame = data as InboundFrame;
      if (frame.category) dispatchMessage(queryClient, data as WSEvent);
      else onControlFrame(frame);
    },
    onOpen: () => {
      sentToken = undefined;
      const reconnecting = everOpened;
      everOpened = true;
      void syncIdentity().then(() => {
        if (!reconnecting || socket.status() !== 'open' || channels.size === 0) return;
        clearTimeout(reconnectAckTimer);
        pendingAcks = new Set(channels.keys());
        reconnectAckTimer = setTimeout(finishReconnect, timing.reconnectAckTimeoutMs);
      });
    },
    onClose: () => {
      clearTimeout(pongTimer);
      sentToken = undefined;
      sentSubscribes.clear();
    },
  });

  return {
    connect: () => {
      socket.connect();
      if (heartbeat) return;
      heartbeat = setInterval(() => {
        clearTimeout(pongTimer);
        if (socket.status() !== 'open') return;
        void syncIdentity();
        socket.send({ action: 'ping' });
        pongTimer = setTimeout(() => socket.reconnect(), timing.pongTimeoutMs);
      }, timing.heartbeatMs);
    },
    close: () => {
      clearInterval(heartbeat);
      heartbeat = undefined;
      clearTimeout(pongTimer);
      clearTimeout(reconnectAckTimer);
      clearTimeout(syncRetryTimer);
      clearTimeout(resendRetryTimer);
      pendingAcks = null;
      socket.close();
    },
    syncIdentity,
    subscribe: (channel) => {
      const refs = channels.get(channel) ?? 0;
      channels.set(channel, refs + 1);
      if (refs === 0 && isReady()) sendSubscribe(channel);
    },
    unsubscribe: (channel) => {
      const refs = channels.get(channel) ?? 0;
      if (refs === 0) return;
      if (refs > 1) return void channels.set(channel, refs - 1);
      channels.delete(channel);
      if (sentSubscribes.delete(channel)) sendRelease({ action: 'unsubscribe', channel });
    },
  };
};
